import { useState } from 'react';
import { Box, Card, CardContent, Typography, Stack, Chip, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { AmFilterBar, type AmFilters } from '../../components/AmFilterBar';
import { amStationOpsApi } from '../../api/amStationOps.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const k = (n = 0) => (n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${Math.round(n / 1000)}k`);

/** Tiny grouped bar row (paid vs pending) without a chart lib. */
function BarRow({ label, paid, pending, max }: { label: string; paid: number; pending?: number; max: number }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" justifyContent="space-between"><Typography variant="caption">{label}</Typography><Typography variant="caption" fontWeight={600}>{k(paid)}{pending ? ` · ${k(pending)} due` : ''}</Typography></Stack>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'success.main', width: `${(paid / max) * 100}%`, minWidth: paid ? 3 : 0 }} />
        {pending != null && <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'warning.main', width: `${(pending / max) * 100}%`, minWidth: pending ? 3 : 0 }} />}
      </Box>
    </Box>
  );
}

export function AmAnalyticsPage() {
  const [filters, setFilters] = useState<AmFilters>({});
  const { data, isLoading } = useQuery({ queryKey: ['am-deep', filters], queryFn: () => amStationOpsApi.deep({ clientId: filters.clientId || undefined, city: filters.city || undefined, propertyType: filters.propertyType || undefined, ownerType: filters.ownerType || undefined, connectionType: filters.connectionType || undefined, from: filters.from || undefined, to: filters.to || undefined, months: 6 }) });
  const trendLabel = filters.from || filters.to ? `${filters.from ?? '…'} → ${filters.to ?? '…'}` : '6 months';

  const rentMax = Math.max(1, ...(data?.rentByMonth ?? []).flatMap((m) => [m.paid, m.pending]));
  const cityMax = Math.max(1, ...(data?.rentByCity ?? []).map((c) => c.paid));

  return (
    <>
      <PageHeader title="Asset Management — Deep Analytics" subtitle="Portfolio mix, trends, client & city breakdowns, agreement pipeline, DISCOM mix." />
      <AmFilterBar value={filters} onChange={setFilters} show={['client', 'city', 'property', 'owner', 'connection', 'dates']} />
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {data && (
        <Stack spacing={2}>
          {/* Totals */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Card sx={{ flex: '1 1 160px' }}><CardContent sx={{ p: 1.75 }}><Typography variant="h6" fontWeight={800}>{data.totals.stationsActive}</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Active stations</Typography></CardContent></Card>
            <Card sx={{ flex: '1 1 160px' }}><CardContent sx={{ p: 1.75 }}><Typography variant="h6" fontWeight={800} color="success.main">{inr(data.totals.rentPaidTotal)}</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Rent paid to owners (total)</Typography></CardContent></Card>
            <Card sx={{ flex: '1 1 160px' }}><CardContent sx={{ p: 1.75 }}><Typography variant="h6" fontWeight={800} color="warning.main">{inr(data.totals.electricityPaidTotal)}</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Electricity paid (total)</Typography></CardContent></Card>
            <Card sx={{ flex: '1 1 160px' }}><CardContent sx={{ p: 1.75 }}><Typography variant="h6" fontWeight={800} color="error.main">{inr(data.totals.overdueTotal)}</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Overdue to pay</Typography></CardContent></Card>
          </Stack>

          {/* Station portfolio — property / owner / chargers / connections / renewal */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Card sx={{ flex: 1 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Stations by property type</Typography>
              {data.stationsByProperty.length === 0 ? <Typography variant="body2" color="text.secondary">No data.</Typography>
                : data.stationsByProperty.map((p) => <Stack key={p.type} direction="row" justifyContent="space-between"><Typography variant="body2">{p.type}</Typography><Typography variant="body2" fontWeight={600}>{p.count}</Typography></Stack>)}
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">Owner structure</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={0.5}>{data.ownerTypeMix.map((o) => <Chip key={o.type} size="small" label={`${o.type}: ${o.count}`} />)}</Stack>
            </CardContent></Card>
            <Card sx={{ flex: 1 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Charger inventory</Typography>
              <Stack direction="row" spacing={3}>
                <Box><Typography variant="h6" fontWeight={800}>{data.chargers.total}</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Chargers</Typography></Box>
                <Box><Typography variant="h6" fontWeight={800}>{data.chargers.totalCapacityKw} kW</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Total capacity</Typography></Box>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{data.chargers.byType.map((t) => <Chip key={t.type} size="small" variant="outlined" label={`${t.type}: ${t.count}`} />)}</Stack>
            </CardContent></Card>
            <Card sx={{ flex: 1 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Electricity connections</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>{data.connections.byType.map((t) => <Chip key={t.type} size="small" color={t.type === 'HT' ? 'warning' : 'default'} label={`${t.type}: ${t.count}`} />)}</Stack>
              <Typography variant="caption" color="text.secondary">Top DISCOMs</Typography>
              {data.connections.byDiscom.slice(0, 6).map((d) => <Stack key={d.discom} direction="row" justifyContent="space-between"><Typography variant="body2">{d.discom}</Typography><Typography variant="body2" fontWeight={600}>{d.count}</Typography></Stack>)}
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">Agreement renewal</Typography>
              <Stack direction="row" spacing={1} mt={0.5}><Chip size="small" color="success" label={`Auto: ${data.agreementAutoRenewal.autoRenew}`} /><Chip size="small" label={`Manual: ${data.agreementAutoRenewal.manual}`} /></Stack>
            </CardContent></Card>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Rent trend */}
            <Card sx={{ flex: 1 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Rent — paid vs due ({trendLabel})</Typography>
              {data.rentByMonth.length === 0 ? <Typography variant="body2" color="text.secondary">No data.</Typography>
                : data.rentByMonth.map((m) => <BarRow key={m.month} label={m.month} paid={m.paid} pending={m.pending} max={rentMax} />)}
              <Stack direction="row" spacing={2} mt={1}><Chip size="small" label="Paid" color="success" /><Chip size="small" label="Due" color="warning" /></Stack>
            </CardContent></Card>
            {/* Month-wise electricity comparison */}
            <Card sx={{ flex: 1 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Month-wise Electricity comparison</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Month</TableCell><TableCell align="right">Units</TableCell><TableCell align="right">Billed</TableCell><TableCell align="right">Paid</TableCell><TableCell align="right">vs prev</TableCell></TableRow></TableHead>
                <TableBody>
                  {data.electricityByMonth.map((m, i) => {
                    const prev = data.electricityByMonth[i - 1];
                    const d = prev && prev.billed ? ((m.billed - prev.billed) / prev.billed) * 100 : null;
                    return (
                      <TableRow key={m.month}>
                        <TableCell>{m.month}</TableCell>
                        <TableCell align="right">{m.units.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">{inr(m.billed)}</TableCell>
                        <TableCell align="right">{inr(m.paid)}</TableCell>
                        <TableCell align="right">{d == null ? '—' : <Chip size="small" color={d > 0 ? 'error' : 'success'} label={`${d > 0 ? '▲' : '▼'} ${Math.abs(d).toFixed(0)}%`} />}</TableCell>
                      </TableRow>
                    );
                  })}
                  {data.electricityByMonth.length === 0 && <TableRow><TableCell colSpan={5}>No data.</TableCell></TableRow>}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary">"vs prev" compares billed amount to the previous month (▲ = higher spend).</Typography>
            </CardContent></Card>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* By client */}
            <Card sx={{ flex: 1.4 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Top clients (paid on their behalf)</Typography>
              <Table size="small"><TableHead><TableRow><TableCell>Client</TableCell><TableCell align="right">Rent</TableCell><TableCell align="right">Electricity</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
                <TableBody>
                  {data.byClient.map((c) => <TableRow key={c.clientId}><TableCell>{c.clientName}</TableCell><TableCell align="right">{inr(c.rentPaid)}</TableCell><TableCell align="right">{inr(c.electricityPaid)}</TableCell><TableCell align="right"><b>{inr(c.rentPaid + c.electricityPaid)}</b></TableCell></TableRow>)}
                  {data.byClient.length === 0 && <TableRow><TableCell colSpan={4}>No data.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent></Card>
            {/* By city */}
            <Card sx={{ flex: 1 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Rent by city</Typography>
              {data.rentByCity.length === 0 ? <Typography variant="body2" color="text.secondary">No data.</Typography>
                : data.rentByCity.map((c) => <BarRow key={c.city} label={c.city} paid={c.paid} max={cityMax} />)}
            </CardContent></Card>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Agreement pipeline */}
            <Card sx={{ flex: 1 }}><CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Agreement pipeline</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
                {Object.entries(data.agreements.byStatus).map(([s, c]) => <Chip key={s} size="small" label={`${s.replace(/_/g, ' ')}: ${c}`} />)}
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">Expiring soon</Typography>
              <Stack direction="row" spacing={1} mt={0.5}>
                <Chip size="small" color="error" label={`≤30d: ${data.agreements.expiry.d30}`} />
                <Chip size="small" color="warning" label={`31–60d: ${data.agreements.expiry.d60}`} />
                <Chip size="small" label={`61–90d: ${data.agreements.expiry.d90}`} />
              </Stack>
            </CardContent></Card>
          </Stack>
        </Stack>
      )}
    </>
  );
}
